/**
 * Arrêts — référentiel central (MBC phase 1).
 * Carte et tableau consomment le MÊME état `stops` chargé depuis /api/stops.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Power, RefreshCw, Route, FileSpreadsheet, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadReferentialExport } from "@/lib/downloadReferential";
import { PageShell, PageHeader, SectionCard, ModernTable, SearchBar, ActionButton, StatusBadge, EmptyState } from "@/components/ds";
import StopsReferentialMap from "@/components/stops/StopsReferentialMap";
import StopFormModal from "@/components/stops/StopFormModal";
import ConfirmDeleteDialog from "@/components/stops/ConfirmDeleteDialog";
import StopsImportDialog from "@/components/stops/StopsImportDialog";
import IconBtn from "@/components/stops/IconBtn";
import { formatCoord } from "@/lib/stopsUnits";
import { styleOf, markerHtml } from "@/lib/stopStyles";

const COLUMNS = [
  { key: "name", label: "Arrêt" },
  { key: "coords", label: "Coordonnées" },
  { key: "address", label: "Adresse" },
  { key: "active", label: "Statut" },
  { key: "actions", label: "", align: "right" },
];

function errText(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && d.message) return d.message;
  return formatApiErrorDetail(d);
}

export default function AdminStops() {
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [focusId, setFocusId] = useState(null);
  const [form, setForm] = useState({ open: false, initial: null });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/stops");
      setStops(r.data?.items || []);
    } catch (e) {
      toast.error(errText(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? stops.filter((s) => s.name.toLowerCase().includes(n)) : stops;
  }, [stops, q]);

  const upsertLocal = (doc) => setStops((prev) => {
    const i = prev.findIndex((s) => s.id === doc.id);
    const next = i >= 0 ? prev.map((s) => (s.id === doc.id ? doc : s)) : [...prev, doc];
    return next.sort((a, b) => a.name.localeCompare(b.name, "fr"));
  });

  const submitForm = async (payload) => {
    setSaving(true);
    try {
      if (form.initial?.id) {
        const r = await api.patch(`/stops/${form.initial.id}`, payload);
        upsertLocal(r.data); toast.success("Arrêt modifié");
      } else {
        const r = await api.post("/stops", { ...payload, source: form.initial?.source || "MANUAL" });
        upsertLocal(r.data); setFocusId(r.data.id); toast.success("Arrêt créé");
      }
      setForm({ open: false, initial: null });
    } catch (e) {
      toast.error(errText(e));
    } finally { setSaving(false); }
  };

  const onMapClick = (lat, lng) => setForm({ open: true, initial: { latitude: +lat.toFixed(6), longitude: +lng.toFixed(6), source: "MAP" } });

  const onDragEnd = async (stop, lat, lng) => {
    try {
      const r = await api.patch(`/stops/${stop.id}`, { latitude: +lat.toFixed(6), longitude: +lng.toFixed(6) });
      upsertLocal(r.data); toast.success(`Position de « ${stop.name} » mise à jour`);
    } catch (e) {
      toast.error(errText(e)); load();
    }
  };

  const toggleActive = async (stop) => {
    try {
      const r = await api.patch(`/stops/${stop.id}`, { active: !stop.active });
      upsertLocal(r.data); toast.success(r.data.active ? "Arrêt réactivé" : "Arrêt désactivé");
    } catch (e) { toast.error(errText(e)); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/stops/${toDelete.id}`);
      setStops((prev) => prev.filter((s) => s.id !== toDelete.id));
      if (focusId === toDelete.id) setFocusId(null);
      toast.success("Arrêt supprimé"); setToDelete(null);
    } catch (e) {
      toast.error(errText(e), { duration: 6000 }); setToDelete(null);
    } finally { setDeleting(false); }
  };

  const renderCell = (row, col) => {
    switch (col.key) {
      case "name":
        return (
          <span className="font-bold inline-flex items-center gap-2" data-testid={`stop-row-name-${row.id}`} title={styleOf(row).categoryLabel}>
            <span dangerouslySetInnerHTML={{ __html: markerHtml({ ...styleOf(row), size: 16 }) }} />{row.name}
          </span>
        );
      case "coords":
        return <span className="font-mono text-xs text-zinc-600">{formatCoord(row.latitude)}, {formatCoord(row.longitude)}</span>;
      case "address":
        return <span className="text-xs text-zinc-600">{row.address || "—"}</span>;
      case "active":
        return (
          <div className="flex flex-wrap gap-1">
            <StatusBadge tone={row.active ? "success" : "neutral"} label={row.active ? "Actif" : "Inactif"} />
            {row.coord_conflict?.status === "pending" && <StatusBadge tone="warning" label="À valider" testId={`stop-conflict-${row.id}`} />}
            {row.flags?.includes("shared_coords") && <StatusBadge tone="info" label="Coord. partagées" withDot={false} />}
          </div>
        );
      case "actions":
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <IconBtn icon={Pencil} title="Modifier" testId={`stop-edit-${row.id}`} onClick={() => setForm({ open: true, initial: row })} />
            <IconBtn icon={Power} title={row.active ? "Désactiver" : "Réactiver"} testId={`stop-toggle-${row.id}`} onClick={() => toggleActive(row)} tone={row.active ? "warn" : "ok"} />
            <IconBtn icon={Trash2} title="Supprimer" testId={`stop-delete-${row.id}`} onClick={() => setToDelete(row)} tone="danger" />
          </div>
        );
      default:
        return row[col.key];
    }
  };

  return (
    <PageShell testId="admin-stops-page">
      <PageHeader
        eyebrow="Planification"
        title="Arrêts"
        subtitle="Référentiel central des arrêts. La carte et le tableau partagent la même source de données : cliquez sur la carte pour créer, glissez un marqueur pour repositionner."
        actions={
          <>
            <ActionButton variant="ghost" icon={RefreshCw} onClick={load} loading={loading} testId="stops-refresh">Actualiser</ActionButton>
            <ActionButton variant="ghost" icon={Route} to="/admin/inter-stops" testId="stops-goto-inter">Inter-arrêts</ActionButton>
            <ActionButton variant="ghost" icon={FileSpreadsheet} onClick={() => setImportOpen(true)} testId="stops-import-btn">Importer Excel</ActionButton>
            <ActionButton variant="ghost" icon={Download} onClick={downloadReferentialExport} testId="stops-export-btn">Exporter Excel</ActionButton>
            <ActionButton variant="primary" icon={Plus} onClick={() => setForm({ open: true, initial: null })} testId="stops-add-btn">Ajouter un arrêt</ActionButton>
          </>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <SectionCard title="Carte" icon={MapPin} tone="navy" className="xl:col-span-3" bodyClassName="h-[560px]"
          actions={<span className="text-[10px] font-mono opacity-70" data-testid="stops-map-count">{stops.length} arrêt{stops.length > 1 ? "s" : ""}</span>}>
          <StopsReferentialMap
            stops={stops}
            focusStopId={focusId}
            draggable
            onMapClick={onMapClick}
            onMarkerClick={(s) => { setFocusId(s.id); setForm({ open: true, initial: s }); }}
            onMarkerDragEnd={onDragEnd}
          />
        </SectionCard>

        <SectionCard title="Liste des arrêts" icon={MapPin} tone="white" className="xl:col-span-2"
          actions={<span className="text-[10px] font-mono text-zinc-500" data-testid="stops-table-count">{filtered.length}/{stops.length}</span>}>
          <div className="p-3 border-b border-zinc-200">
            <SearchBar value={q} onChange={setQ} placeholder="Rechercher un arrêt par nom…" testId="stops-search" dense />
          </div>
          {!loading && stops.length === 0 ? (
            <EmptyState icon={MapPin} title="Aucun arrêt" description="Créez votre premier arrêt via le bouton « Ajouter un arrêt » ou en cliquant sur la carte."
              action={<ActionButton variant="primary" icon={Plus} onClick={() => setForm({ open: true, initial: null })} testId="stops-empty-add">Ajouter un arrêt</ActionButton>} />
          ) : (
            <div className="max-h-[500px] overflow-auto">
              <ModernTable
                columns={COLUMNS} rows={filtered} renderCell={renderCell} dense stickyHeader
                testId="stops-table" bodyTestId="stops-table-body"
                emptyMessage="Aucun arrêt ne correspond à la recherche."
                onRowClick={(row) => setFocusId(row.id)}
                rowClassName={(row) => (row.id === focusId ? "bg-[#FFF7CC]" : "")}
                className="border-0"
              />
            </div>
          )}
        </SectionCard>
      </div>

      <StopsImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      <StopFormModal open={form.open} initial={form.initial} saving={saving} onClose={() => setForm({ open: false, initial: null })} onSubmit={submitForm} />
      <ConfirmDeleteDialog
        open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} loading={deleting} onConfirm={confirmDelete}
        title="Supprimer cet arrêt ?"
        description={toDelete ? `« ${toDelete.name} » sera définitivement supprimé du référentiel. La suppression est refusée si l'arrêt est utilisé par un inter-arrêt ou une tournée.` : ""}
        testIdPrefix="stop-delete"
      />
    </PageShell>
  );
}
